/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useTranslatedText } from '../../../hooks/common/use-translated-text'
import { cypressId } from '../../../utils/cypress-attribute'
import { UiIcon } from '../icons/ui-icon'
import styles from './badge-picker-button.module.scss'
import type { EmojiClickEventDetail } from 'emoji-picker-element/shared'
import React, { Fragment, Suspense, useCallback, useRef, useState } from 'react'
import { Button, ButtonGroup, Overlay, Popover } from 'react-bootstrap'
import { EmojiSmile as IconEmojiSmile, X as IconX } from 'react-bootstrap-icons'

// The picker uses custom elements, which are only available in the browser
const EmojiPicker = React.lazy(() => import('./emoji-picker').then((module) => ({ default: module.EmojiPicker })))

export interface BadgePickerButtonProps {
  badge: string
  onChange: (badge: string) => void
  disabled?: boolean
}

/**
 * Renders a button that shows the current badge of a note and opens an emoji picker to change it.
 *
 * @param badge The current badge, empty for none
 * @param onChange Is called with the newly selected badge or an empty string if the badge was removed
 * @param disabled Whether the badge can't be changed
 */
export const BadgePickerButton: React.FC<BadgePickerButtonProps> = ({ badge, onChange, disabled }) => {
  const [showPicker, setShowPicker] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const onEmojiSelected = useCallback(
    (detail: EmojiClickEventDetail) => {
      setShowPicker(false)
      onChange(detail.unicode ?? '')
    },
    [onChange]
  )
  const togglePicker = useCallback(() => setShowPicker((old) => !old), [])
  const hidePicker = useCallback(() => setShowPicker(false), [])
  const removeBadge = useCallback(() => onChange(''), [onChange])

  const chooseBadgeText = useTranslatedText('pageSettings.badge.choose')
  const removeBadgeText = useTranslatedText('pageSettings.badge.remove')

  return (
    <Fragment>
      {/* The overlay is rendered in here, so that it stays inside the focus trap of a surrounding modal */}
      <div ref={containerRef} />
      <ButtonGroup>
        <Button
          ref={buttonRef}
          variant={'outline-secondary'}
          className={styles.button}
          onClick={togglePicker}
          disabled={disabled}
          title={chooseBadgeText}
          aria-label={chooseBadgeText}
          {...cypressId('badge-picker-button')}>
          {badge === '' ? <UiIcon icon={IconEmojiSmile} /> : <span className={styles.badge}>{badge}</span>}
        </Button>
        {badge !== '' && (
          <Button
            variant={'outline-secondary'}
            onClick={removeBadge}
            disabled={disabled}
            title={removeBadgeText}
            aria-label={removeBadgeText}>
            <UiIcon icon={IconX} />
          </Button>
        )}
      </ButtonGroup>
      <Overlay
        show={showPicker}
        onHide={hidePicker}
        placement={'bottom-start'}
        flip={true}
        target={buttonRef.current}
        container={containerRef.current}
        rootClose={true}>
        <Popover className={styles.popover}>
          <Popover.Body>
            <Suspense fallback={null}>
              <EmojiPicker onEmojiSelected={onEmojiSelected} />
            </Suspense>
          </Popover.Body>
        </Popover>
      </Overlay>
    </Fragment>
  )
}
